import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Button Component - INFLUENCITY-TIER
 * 
 * Contract:
 * - Primary uses gradient for premium feel
 * - Focus rings clearly visible
 * - Disabled states readable (not washed out)
 * - Consistent sizing, radius, shadows
 * 
 * All colors route through granullo.tokens.css
 */

const buttonVariants = cva(
  // Base styles
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-[var(--g-dur-2)] ease-[var(--g-ease-out)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-focus-ring)] disabled:pointer-events-none disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary: solid blue with VISIBLE hover affordance (shadow + lift)
        default:
          "rounded-[var(--g-btn-radius)] bg-[var(--g-btn-primary-bg)] text-[var(--g-on-primary)] border border-[var(--g-btn-primary-border)] shadow-[var(--g-btn-shadow)] hover:shadow-[var(--g-btn-shadow-hover)] hover:-translate-y-[1px] active:translate-y-0 active:shadow-[var(--g-btn-shadow)]",
        // Destructive: rose danger - uses danger shadow token
        destructive:
          "rounded-[var(--g-btn-radius)] bg-[var(--g-danger)] text-white shadow-[var(--g-shadow-low)] hover:shadow-[var(--g-shadow-danger-hover)] hover:-translate-y-[1px] active:translate-y-0",
        // Outline: transparent with border
        outline:
          "rounded-[var(--g-btn-radius)] border border-[var(--g-btn-secondary-border)] bg-[var(--g-btn-secondary-bg)] text-[var(--g-btn-secondary-text)] shadow-[var(--g-shadow-low)] hover:bg-[var(--g-nav-item-bg-hover)] hover:-translate-y-[1px] active:translate-y-0",
        // Secondary: glass surface
        secondary:
          "rounded-[var(--g-btn-radius)] bg-[var(--g-btn-secondary-bg)] text-[var(--g-btn-secondary-text)] border border-[var(--g-btn-secondary-border)] shadow-[var(--g-shadow-low)] hover:bg-[var(--g-nav-item-bg-hover)] hover:-translate-y-[1px] active:translate-y-0",
        // Ghost: minimal
        ghost: 
          "rounded-[var(--g-btn-radius)] text-[var(--g-text)] hover:bg-[var(--g-nav-item-bg-hover)]",
        // Link: text only
        link: 
          "text-[var(--g-primary)] underline-offset-4 hover:underline",
        // Calm: soft, non-urgent
        calm:
          "rounded-[var(--g-btn-radius)] bg-[var(--g-btn-calm-bg)] text-[var(--g-btn-calm-text)] border border-[var(--g-btn-calm-border)] hover:brightness-95 hover:-translate-y-[1px] active:translate-y-0",
        // Danger outline: rose outline style
        "danger-outline":
          "rounded-[var(--g-btn-radius)] border border-[var(--g-badge-danger-border)] bg-[var(--g-btn-danger-bg)] text-[var(--g-btn-danger-text)] hover:brightness-95 hover:-translate-y-[1px] active:translate-y-0",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-10 px-6",
        xl: "h-11 px-8 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, disabled, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(
        buttonVariants({ variant, size, className }),
        disabled && "bg-[var(--g-btn-disabled-bg)] text-[var(--g-btn-disabled-text)] border-[var(--g-btn-disabled-border)] cursor-not-allowed hover:brightness-100"
      )}
      ref={ref}
      disabled={disabled}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
